import { ReactElement, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { toCurrency } from 'utils/numbers';
import { Component as PlusIcon } from 'icons/icon-ui--plus.svg';
import { Component as ClockIcon } from 'icons/icon-ui--clock.svg';
import { Component as SaleTagIcon } from 'icons/icon-ui--sale-tag.svg';
import { Component as BoxIcon } from 'icons/icon-box.svg';
import { parseZone } from 'moment';
import DOMPurify from 'dompurify';
import { useFlags } from '@flagsmith/flagsmith/react';
import { ProductRating } from './ReviewsContent/ProductRating';
import { BreakpointMedia } from '../../utils/config';
import { NutritionLabelModal } from '../shared/modal/NutritionLabelModal';
import { Product, Sku } from '../../models/product.types';
import { Button } from '../shared/button/Button';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { useAuth } from '../../contexts/AuthContext';
import { QuantitySelector } from '../shared/quantity-selector/QuantitySelector';
import { getFormattedDeliveryDateRangeText } from '../../utils/dateTimes';
import { useUser } from '../../contexts/UserContext';
import { useNotification } from '../../contexts/NotificationContext';
import { MenuCartItem } from '../../models/menu.types';
import { SubscriptionStatus } from '../../models/user.types';
import { Toast, ToastVariant } from '../shared/notification/Toast';
import { Breadcrumb } from './Breadcrumb';
import { useNewProspectWelcomeToast } from '../../hooks/useNewProspectWelcomeToast';
import styles from './ProductDisplayPage.module.scss';
import { gtmTrackAddToCart } from '../../services/gtm/gtm';
import { parsePricePerRecipe } from '../../utils/prices';

type Props = {
  sku: Sku,
  product: Product,
  isRte: boolean,
  menuCartItem?: MenuCartItem | null,
}

export function ProductDetails({
  sku,
  product,
  isRte,
  menuCartItem,
}: Props): ReactElement {
  const [isNutritionLabelOpen, setIsNutritionLabelOpen] = useState(false);

  const { isLoggedIn } = useAuth();
  const {
    addToCart,
    cart,
    menu,
    reactivateSubscription,
    updateCartQuantity,
    recalculateCart,
  } = useMenuWithCart();
  const { user } = useUser();
  const { showNotification } = useNotification();

  useNewProspectWelcomeToast(cart);

  const isMealKit = sku.product_type_name === 'Meal Kit';
  const isMealKitOrRte = isMealKit || isRte;
  const isOnSale = !!(menuCartItem?.saleTagEnabled && menuCartItem?.salePrice);

  const pricePerServingText = isMealKitOrRte
    ? ` ${isOnSale && menuCartItem?.salePricePerServing ? menuCartItem.salePricePerServing : toCurrency(sku.subscription_price_per_serving)} / serving`
    : ` / ${sku.spec}`;

  const flags = useFlags(['display-customer-reviews-pdp', 'add-to-cart-subtle']);
  const customerReviewFlag = flags['display-customer-reviews-pdp'];
  const showCustomerReviewsPdp = customerReviewFlag.enabled && customerReviewFlag.value === 'show';

  const stackElements = useMediaQuery({ query: BreakpointMedia.MAX_MD });
  const isMobileWidth = useMediaQuery({ query: BreakpointMedia.MAX_SM });

  const priceDisplay = (
    <div>
      {isOnSale ? (
        <>
          <span className={styles.sale_price}>{menuCartItem?.salePrice}</span>
          <span className={styles.pre_sale_price}>{toCurrency(sku.price)}</span>
        </>
      ) : (
        <span className={styles.total_price}>{toCurrency(sku.price)}</span>
      )}
      <span className={styles.price_per_serving}>
        {isMealKitOrRte && ' ('}
        <span className={isOnSale && isMealKitOrRte ? styles.sale_text : ''}>
          {pricePerServingText}
        </span>
        {isMealKitOrRte && ' )'}
      </span>
    </div>
  );
  const menuSelectorDate = menu?.deliveryOn ? parseZone(menu?.deliveryOn)
    .format('ddd, MMM D, YYYY') : '';
  const nutrition = {
    Cal: Math.round(sku?.calories ?? 0),
    Fat: `${Math.round(Number(sku?.fat ?? 0))}g`,
    Carbs: `${Math.round(Number(sku?.carbs ?? 0))}g`,
    Protein: `${Math.round(Number(sku?.protein ?? 0))}g`,
    Fiber: `${Math.round(Number(sku?.fiber ?? 0))}g`,
  };
  const badgeTags = sku?.tags?.filter((tag) => {
    if (tag.name === 'Low Sodium') {
      tag.name = 'Sodium Smart';
    }
    return ['Gluten-Free', 'High-Protein', 'High Fiber', 'Sodium Smart'].includes(tag.name);
  });
  const isInactiveSubscription = user?.status
    && (user.status === SubscriptionStatus.RETURNING
      || user.status === SubscriptionStatus.CANCELLED
      || user.status === SubscriptionStatus.PAUSED);

  // dompurify sanitizer for description input that will be rendered in HTML
  const cleanDescription = DOMPurify.sanitize(sku.description || '', {
    ALLOWED_TAGS: ['p', 'br', 'div', 'span', 'strong', 'b', 'i', 'u',
      'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  });

  const addToCartSubtle = flags['add-to-cart-subtle'].enabled && flags['add-to-cart-subtle'].value === 'show';
  const suppressDrawer = !isLoggedIn && addToCartSubtle && isMobileWidth;

  const handleAddToCart = async () => {
    if (isInactiveSubscription) {
      await reactivateSubscription();
      showNotification({
        content: <Toast variant={ToastVariant.ACCOUNT_ACTIVE} />,
        duration: 5000,
      });
    }
    if (cart?.isSkipped) {
      await recalculateCart({
        ...cart,
        isSkipped: false,
      });
      showNotification({
        content: <Toast variant={ToastVariant.CART_UNSKIPPED_AND_ADDED} />,
        duration: 5000,
      });
    }
    if (menuCartItem?.isInStock && (menuCartItem?.quantity ?? 0) === 0) {
      if (suppressDrawer) {
        showNotification({
          content: <Toast variant={ToastVariant.ITEM_ADDED_TO_CART} />,
          duration: 4000,
        });
      }
      addToCart(menuCartItem, false, suppressDrawer);
    }
  };

  const cartActionButton = () => {
    const quantity = menuCartItem?.quantity ?? 0;
    const isSkipped = cart?.isSkipped;

    if (menuCartItem && !menuCartItem.isInStock) {
      return (
        <Button
          text="Sold Out"
          onClick={() => console.log('Sold Out')}
          isEnabled={false}
        />
      );
    }
    if (isInactiveSubscription) {
      return (
        <Button
          text="Re-Activate Account & Add To Cart"
          icon={PlusIcon}
          onClick={handleAddToCart}
          isEnabled
          className={styles.atc_button}
        />
      );
    }
    if (isSkipped) {
      return (
        <Button
          text="Unskip & Add to Cart"
          icon={PlusIcon}
          onClick={handleAddToCart}
          isEnabled
          className={styles.atc_button}
        />
      );
    }
    if (quantity > 0) {
      return (
        <QuantitySelector
          quantity={quantity}
          onAddQuantity={() => {
            if (menuCartItem) {
              if (!isLoggedIn) {
                gtmTrackAddToCart({
                  price: parsePricePerRecipe(isOnSale
                  && menuCartItem?.salePrice ? menuCartItem.salePrice : sku.price),
                  currency: 'USD',
                  ecomPagetype: 'product',
                  item: {
                    itemName: product.title,
                    itemId: String(sku.id),
                    price: parsePricePerRecipe(isOnSale
                    && menuCartItem?.salePrice ? menuCartItem.salePrice : sku.price),
                    quantity: 1,
                  },
                });
              }
              updateCartQuantity(menuCartItem, quantity + 1);
            }
          }}
          onSubtractQuantity={() => menuCartItem && updateCartQuantity(menuCartItem, quantity - 1)}
        />
      );
    }
    return (
      <Button
        text="Add"
        icon={PlusIcon}
        onClick={() => {
          if (!isLoggedIn) {
            gtmTrackAddToCart({
              price: parsePricePerRecipe(isOnSale
              && menuCartItem?.salePrice ? menuCartItem.salePrice : sku.price),
              currency: 'USD',
              ecomPagetype: 'product',
              item: {
                itemName: product.title,
                itemId: String(sku.id),
                price: parsePricePerRecipe(isOnSale
                && menuCartItem?.salePrice ? menuCartItem.salePrice : sku.price),
                quantity: 1,
              },
            });
          }
          handleAddToCart();
        }}
        isEnabled
        className={styles.atc_button}
      />
    );
  };

  return (
    <div className={styles.details_layout_container}>
      <div className={styles.details_container}>
        {!stackElements && (
          <Breadcrumb pageTitle={product.title} />
        )}
        {!isMealKitOrRte && sku.brand && (
          <h2
            className={styles.product_subtitle}
            style={stackElements ? {} : { marginTop: '16px' }}
          >
            {sku.brand}
          </h2>
        )}
        <h1 className={styles.product_title}>{product.title}</h1>
        {product.subtitle && (
          <h2 className={styles.product_subtitle}>
            {product.subtitle.charAt(0)
              .toUpperCase() + product.subtitle.slice(1)}
          </h2>
        )}
        <div style={{ paddingTop: '7px' }}>
          {showCustomerReviewsPdp && (
            <ProductRating
              productId={product.id}
              className={styles.product_rating_pdp}
            />
          )}
        </div>
        {!isMobileWidth && (
          <div
            className={`${styles.product_pricing_container} ${
              isInactiveSubscription ? styles.vertical_align : ''
            }`}
          >
            {menuCartItem && cartActionButton()}
            {priceDisplay}
            {isOnSale && (
              <div className={styles.sale_tag}>
                <SaleTagIcon />
                SALE
              </div>
            )}
          </div>
        )}
        <div className={styles.bordered_row_container}>
          {isMobileWidth ? (
            <div className={styles.pricing_container}>
              {priceDisplay}
            </div>
          ) : (
            <div className={styles.delivery_date_container}>
              <BoxIcon width="24" />
              <div className={styles.delivery_date}>
                <strong>
                  {isLoggedIn ? menuSelectorDate
                    : getFormattedDeliveryDateRangeText(menu?.deliveryOn || '', false)}
                </strong>
                {' Delivery'}
              </div>
            </div>
          )}
          {sku.product_type_name !== 'Grocery' && (
            <div className={styles.cook_time_container}>
              <ClockIcon style={{ overflow: 'visible' }} />
              <div className={styles.cook_time}>
                <span>
                  {`${sku.prep_and_cook_time} min ${isRte ? 'ready-to-eat' : sku.product_type_name?.toLowerCase()}`}
                </span>
                {!isMobileWidth && <span>{' | '}</span>}
                <span>{`${sku.servings} ${sku.servings === 1 ? 'serving' : 'servings'}`}</span>
              </div>
            </div>
          )}
        </div>

        <div className={styles.nutrition_row_container}>
          {Object.entries(nutrition)
            .filter(([, value]) => value !== 0 && value !== '0g')
            .map(
              ([unit, value], idx) => (
                <span key={unit} className={styles.nutrient}>
                  {idx > 0 && <span className={styles.separator}>•</span>}
                  <span>
                    <span className={styles.nutrient_value}>{value}</span>
                    {' '}
                    <span className={styles.nutrient_unit}>{unit}</span>
                  </span>
                </span>
              ),
            )}
          <div className={styles.nutrition_label_link}>
            <button
              type="button"
              onClick={() => setIsNutritionLabelOpen(true)}
            >
              View Nutrition Label
            </button>
          </div>
          {sku?.nutrition_label && (
            <NutritionLabelModal
              isOpen={isNutritionLabelOpen}
              title={product.title || ''}
              subtitle={product?.subtitle || ''}
              nutritionLabel={sku?.nutrition_label}
              onClose={() => setIsNutritionLabelOpen(false)}
              hasIngredients={sku?.ingredients?.length > 0}
            />
          )}
        </div>

        <div className={styles.description_container}>
          <div
            className={styles.description_content}
            dangerouslySetInnerHTML={{ __html: cleanDescription }}
          />
          {sku?.allergens && (
            <div>
              <strong>Allergens: </strong>
              {sku.allergens}
            </div>
          )}
          {!!sku?.prep_needs?.length && (
            <div>
              <strong>Tools: </strong>
              {sku.prep_needs.join(', ')}
            </div>
          )}
          <div className={styles.badge_tags_container}>
            {badgeTags.map((tag) => (
              <div className={styles.badge_tag} key={tag.id}>
                {tag.name}
              </div>
            ))}
          </div>
        </div>
      </div>
      {(isMobileWidth) && (
        <div className={styles.sticky_footer}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          >
            {menuCartItem && cartActionButton()}
            <div className={styles.delivery_date}>
              <strong>
                {isLoggedIn ? menuSelectorDate
                  : getFormattedDeliveryDateRangeText(menu?.deliveryOn || '', false)}
              </strong>
              {' Delivery'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
